import { useCallback, useEffect, useState, type CSSProperties } from 'react'
import type { ServerApiClient } from '../../../data/ServerApiClient'
import { isClaimFlagged, mapServerClaim, type UnderstandingClaim } from './cfoUnderstanding'

// A compact, reusable "what the AI changed about THIS surface" panel. It fetches the Client
// Understanding ledger, filters to the beliefs relevant to the surface (forecast / valuation / …), and
// lists each with its rationale + Accept (acknowledge — the assumption stays applied but collapses out
// of the panel), Revert (reject the belief) and Redirect (re-author it). Accepted assumptions live
// behind a "Show accepted" toggle and can be re-opened. Additive: renders nothing when the workbook has
// no relevant active beliefs. Self-contained (owns its fetch + mutations + refetch).

export type CfoSurfaceBeliefsProps = {
  appApi: ServerApiClient
  workbookId: string | null
  /** Which active beliefs belong on this surface, e.g. `(c) => c.directiveKind.startsWith('forecast.')`. */
  filter: (claim: UnderstandingClaim) => boolean
  heading: string
}

export function CfoSurfaceBeliefs({ appApi, workbookId, filter, heading }: CfoSurfaceBeliefsProps) {
  const [claims, setClaims] = useState<UnderstandingClaim[]>([])
  const [busyOriginId, setBusyOriginId] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [adjustingOrigin, setAdjustingOrigin] = useState<string | null>(null)
  const [adjustText, setAdjustText] = useState('')
  const [showAccepted, setShowAccepted] = useState(false)

  const refetch = useCallback(() => {
    const wb = (workbookId || '').trim()
    if (!wb) {
      setClaims([])
      setLoaded(true)
      return
    }
    void (async () => {
      try {
        const getBootstrap = (appApi as Partial<ServerApiClient>).getAccountingWorkbookBootstrap
        const provider = typeof getBootstrap === 'function'
          ? (await getBootstrap.call(appApi, wb)).accounting_provider
          : 'quickbooks'
        if (provider !== 'quickbooks') {
          setClaims([])
          return
        }
        const response = await appApi.getQuickBooksUnderstanding(wb)
        setClaims(response.claims.map(mapServerClaim).filter((claim) => claim.status === 'active'))
      } catch {
        setClaims([])
      } finally {
        setLoaded(true)
      }
    })()
  }, [appApi, workbookId])

  useEffect(() => refetch(), [refetch])

  const relevant = claims.filter(filter)
  const open = relevant.filter((c) => !c.acknowledgedAt)
  const accepted = relevant.filter((c) => !!c.acknowledgedAt)
  if (!loaded || relevant.length === 0) return null

  const run = (originId: string, op: () => Promise<unknown>) => {
    setBusyOriginId(originId)
    op()
      .catch(() => undefined)
      .finally(() => {
        setBusyOriginId(null)
        refetch()
      })
  }
  const wb = () => (workbookId || '').trim()
  const revert = (claimId: string, originId: string) =>
    run(originId, () => appApi.rejectUnderstandingClaim(wb(), claimId))
  const accept = (claimId: string, originId: string) =>
    run(originId, () => appApi.ackUnderstandingClaims(wb(), [claimId], true))
  const reopen = (claimId: string, originId: string) =>
    run(originId, () => appApi.ackUnderstandingClaims(wb(), [claimId], false))
  const acceptAll = () =>
    run('__all__', () =>
      appApi.ackUnderstandingClaims(
        wb(),
        open.map((c) => c.id),
        true,
      ),
    )
  const closeAdjust = () => {
    setAdjustingOrigin(null)
    setAdjustText('')
  }
  const sendAdjust = (originId: string) => {
    const text = adjustText.trim()
    if (!text) return
    closeAdjust()
    run(originId, () => appApi.redirectUnderstanding(wb(), originId, text))
  }

  const renderItem = (c: UnderstandingClaim, isAccepted: boolean) => (
    <li key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span style={{ flex: 1, color: isAccepted ? 'var(--muted, #6b7280)' : 'var(--ink-2, #3a3f46)' }}>
          {isAccepted ? (
            <span aria-hidden="true" style={{ color: 'var(--pos, #0e7c86)', marginRight: 6 }}>
              ✓
            </span>
          ) : null}
          {c.assertion}
          {!isAccepted && isClaimFlagged(c) ? (
            <span
              title="Auto-applied by the AI — worth a second look"
              style={{
                marginLeft: 8,
                fontSize: 11,
                fontWeight: 600,
                padding: '1px 7px',
                borderRadius: 999,
                color: 'var(--warn-ink, #92610a)',
                background: 'var(--warn-bg, #fdf3e0)',
                border: '1px solid var(--warn-acc, #f0d8a8)',
                whiteSpace: 'nowrap',
              }}
            >
              Flagged
            </span>
          ) : null}
        </span>
        <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {isAccepted ? (
            <button
              type="button"
              disabled={busyOriginId === c.originId}
              onClick={() => reopen(c.id, c.originId)}
              style={miniBtn('var(--ink-2, #3a3f46)')}
            >
              Reopen
            </button>
          ) : (
            <button
              type="button"
              disabled={busyOriginId === c.originId}
              onClick={() => accept(c.id, c.originId)}
              style={miniBtn('var(--pos, #0e7c86)')}
            >
              Accept
            </button>
          )}
          <button
            type="button"
            disabled={busyOriginId === c.originId}
            onClick={() => revert(c.id, c.originId)}
            style={miniBtn('var(--neg-ink, #b42318)')}
          >
            Revert
          </button>
          <button
            type="button"
            disabled={busyOriginId === c.originId}
            onClick={() => (adjustingOrigin === c.originId ? closeAdjust() : setAdjustingOrigin(c.originId))}
            style={miniBtn('var(--ink-2, #3a3f46)')}
          >
            Tell the AI…
          </button>
        </span>
      </div>
      {adjustingOrigin === c.originId ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={adjustText}
            onChange={(e) => setAdjustText(e.target.value)}
            autoFocus
            rows={2}
            placeholder="Tell the AI how to handle this instead, in your words…"
            style={{
              width: '100%',
              fontSize: 13,
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid var(--hair-strong, #d4d8de)',
              background: 'var(--panel, #fff)',
              color: 'var(--ink, #1b1f24)',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              disabled={busyOriginId === c.originId || !adjustText.trim()}
              onClick={() => sendAdjust(c.originId)}
              style={miniBtn('var(--accent, #3b6ef5)')}
            >
              Send to AI
            </button>
            <button type="button" onClick={closeAdjust} style={{ ...miniBtn('var(--muted, #6b7280)'), marginLeft: 'auto' }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </li>
  )

  // Everything accepted + collapsed: keep just a quiet one-line card so the assumptions stay reachable.
  const collapsed = open.length === 0 && !showAccepted

  return (
    <div
      className="card"
      style={{ padding: '14px 16px', marginBottom: 'var(--s4)', borderRadius: 'var(--r3, 12px)' }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--ink, #1b1f24)',
          marginBottom: collapsed ? 0 : 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span aria-hidden="true" style={{ color: 'var(--accent, #3b6ef5)' }}>
          ✦
        </span>
        <span style={{ flex: 1 }}>
          {heading}
          {collapsed ? (
            <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--muted, #6b7280)' }}>
              — {accepted.length} accepted
            </span>
          ) : null}
        </span>
        {open.length > 1 ? (
          <button type="button" disabled={busyOriginId === '__all__'} onClick={acceptAll} style={miniBtn('var(--pos, #0e7c86)')}>
            Accept all
          </button>
        ) : null}
        {accepted.length > 0 ? (
          <button
            type="button"
            onClick={() => setShowAccepted((v) => !v)}
            style={miniBtn('var(--muted, #6b7280)')}
          >
            {showAccepted ? 'Hide accepted' : `Show accepted (${accepted.length})`}
          </button>
        ) : null}
      </div>
      {collapsed ? null : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {open.map((c) => renderItem(c, false))}
          {showAccepted ? accepted.map((c) => renderItem(c, true)) : null}
        </ul>
      )}
    </div>
  )
}

const miniBtn = (color: string): CSSProperties => ({
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 11px',
  borderRadius: 8,
  border: '1px solid var(--hair-strong, #d4d8de)',
  background: 'var(--panel, #fff)',
  color,
  cursor: 'pointer',
})

export default CfoSurfaceBeliefs
